import { useState, useCallback, useMemo, useEffect, useRef, useContext } from "react"
import { message } from "antd"
import styles from "./index.module.scss"
import PicViewerImageList from "./PicViewerImageList"
import PicViewerImageInfo from "./PicViewerImageInfo"
import PicViewerCenter from "./PicViewerCenter"
import RightSideRender from "./components/RightSideRender"
import { PictureViewerContext, PictureViewerContextProvider } from "./hooks/PictureViewerContext"
import useTemporaryMaterials from './hooks/useTemporaryMaterials'
import eventBus from '@/utils/eventBus';

const MULTIPLE_SLOT_COUNT = 3
const MULTIPLE_MAX_PIN_COUNT = 2

/**
 * PictureViewer 中 `extraImages` 的单项结构。
 *
 * @typedef {Object} PictureViewerExtraImage
 * @property {string} url 参考图地址；支持带 `md5` 查询参数，用于切图时优先匹配同源参考图。
 * @property {string} [label] 参考图名称；并列/对比模式下会显示在画布上，也用于 reference 匹配。
 * @property {string} [groupKey] 参考图分组标识；切换参考图时会优先沿用相同分组。
 * @property {Array<Object>} [annotations] 参考图标注；只读展示时也会透出。
 * @property {string} [maskUrl] 遮罩图地址；当主图打开蒙版时会叠加显示。
 * @property {Object} [_raw] 参考图对应的业务原始数据，组件内部不会处理，仅供调用方透传。
 */

/**
 * PictureViewer 主图数据结构。
 *
 * @typedef {Object} PictureViewerImage
 * @property {string|number} id 图片唯一标识；建议稳定唯一，用于切图、提交和视图状态恢复。
 * @property {string|number} [imageId] 图片业务标识；当 `id` 不存在时会作为补充 key 使用。
 * @property {string} url 主图地址；支持图片和视频。
 * @property {string} [name] 主图名称；会展示在画布右上角。
 * @property {'approved'|'rejected'|'canceled'|null} [status] 当前图片状态。
 * @property {Array<Object>} [annotations] 当前图片标注数据；在“不通过”场景下会随提交一起透出。
 * @property {boolean} [readOnly] 是否只读；只读时不会触发状态提交和标注编辑。
 * @property {boolean} [pinned] 多图模式下是否钉住当前图片。
 * @property {boolean} [shouldCommitOnLeave] 离开当前图片时是否需要触发 `onStatusChange`。
 * @property {Array<PictureViewerExtraImage>} [extraImages] 参考图列表；存在时支持并列/对比模式。
 * @property {Object} [_raw] 当前图片的业务原始数据；组件会在回调中原样回传给调用方。
 */

/**
 * PictureViewer 组件入参。
 *
 * 这是一个通用的图片/视频预览与标注组件，支持：
 * - 单图、并列、对比、多图四种预览模式
 * - `approved / rejected / canceled` 三态状态流转
 * - 标注、反馈、遮罩、参考图切换
 * - SyncLock 同步缩放与拖动
 *
 * 组件本身不处理业务字段映射，只通过回调把通用状态和 `_raw` 数据交回调用方。
 *
 * @typedef {Object} PictureViewerProps
 * @property {Array<PictureViewerImage>} [defaultImages=[]] 初始化图片列表。
 * @property {number} [defaultIndex=0] 初始化选中的主图索引。
 * @property {0|1|2|3} [defaultViewMode=0] 初始化视图模式：`0=parallel`、`1=compare`、`2=single`、`3=multiple`。
 * @property {boolean} [allowStatusDeselect=true] 是否允许在底部状态栏取消当前状态。
 * @property {Array<string>} [floatingLabelToolVisibleStatuses=['rejected']] 哪些状态下显示浮动标注工具。
 * @property {'center'|'right'} [floatingLabelToolPlacement='center'] 浮动标注工具位置。
 * @property {boolean} [enableTemporaryMaterials=false] 显示临时素材区；支持本地图片上传、预览和移除，切换主图保留，关闭/卸载时清空，不提交到业务接口。
 * @property {boolean} [canvasToolsDisabled=false] 是否禁用画布标注工具。
 * @property {boolean} [submitEmptyStatusOnLeave=false] 离开当前图片时，即使未选择状态也调用 `onStatusChange`。
 * @property {boolean} [requireRejectedAnnotationBeforeSwitch=false] 选择“不通过”后，切图/关闭前是否强制要求存在标注。
 * @property {Array<React.ReactNode>} [extraButtons=[]] 渲染在底部操作栏左侧扩展区域的按钮。
 * @property {(payload: { id: string|number, status: 'approved'|'rejected'|'canceled'|null, annotation: Array<Object>|null }, rawData: Object) => Promise<void>|void} [onStatusChange]
 * 状态提交回调。组件会在切图、保存、关闭等时机调用；`annotation` 仅在 `rejected` 时透出，否则为 `null`。
 * @property {(payload: { id: string|number, like: boolean, feedbackText?: string, feedbackId?: string|number }, rawData: Object) => Promise<string|number|void>|string|number|void} [onFeedback]
 * 反馈提交回调。新增反馈时可返回服务端生成的 `feedbackId`，供后续更新复用。
 * @property {() => void} [onCancel] 关闭预览回调。组件在必要的离开校验和提交完成后触发。
 * @property {(image: PictureViewerImage) => Promise<void>|void} [onDownload] Custom download handler. Receives the current image and takes precedence over the default download behavior.
 * @property {(image: PictureViewerImage) => React.ReactNode} [rightSideRender]
 * 右侧扩展区域渲染函数，入参为当前主图数据，适合挂业务信息面板。
 */

/**
 * 通用图片/视频预览组件。
 *
 * 使用建议：
 * - 调用方通过 `defaultImages` 传入统一数据结构；
 * - 业务状态映射放在 `onStatusChange` 中处理，不要把业务字段逻辑下沉到组件内部；
 * - 可编辑场景建议开启 `requireRejectedAnnotationBeforeSwitch`，保证“不通过”时必须标注。
 *
 * @param {PictureViewerProps} props
 */
function PictureViewer (props) {
    const {
        // true: 即使当前未选择状态，离开时也调用提交接口
        submitEmptyStatusOnLeave = false,
        // true: 当前为不通过时，离开当前图片前必须有标注
        requireRejectedAnnotationBeforeSwitch = false,
        onStatusChange,
        onFeedback,
        onCancel,
        onDownload,
        extraButtons = [],
        rightSideRender = null,
        enableTemporaryMaterials = false,
    } = props

    const {
        images, setImages, imagesRef,
        currentIndex, setCurrentIndex, currentIndexRef,
        multipleSlots, setMultipleSlots, multipleSlotsRef,
        viewMode, setViewMode,
        currentImage, hasNext, hasPrev,
    } = useContext(PictureViewerContext)

    const {
        previewImage, add: addTemporaryMaterials, remove: removeTemporaryMaterial,
        clear: clearTemporaryMaterials, select: selectTemporaryMaterial,
    } = useTemporaryMaterials(enableTemporaryMaterials, currentImage)

    const [isSwitching, setIsSwitching] = useState(false)

    const isMountedRef = useRef(true)
    const prevViewModeRef = useRef(viewMode)

    const updateMultipleSlots = useCallback((updater) => {
        setMultipleSlots(prev => {
            const nextValue = typeof updater === 'function' ? updater(prev) : updater
            multipleSlotsRef.current = nextValue
            return nextValue
        })
    }, [])

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (previewImage?.extraImages?.length === 0 && ['parallel', 'compare'].includes(viewMode)) {
            setViewMode('single')
        }
    }, [previewImage, setViewMode, viewMode])

    useEffect(() => {
        const prevViewMode = prevViewModeRef.current
        prevViewModeRef.current = viewMode
        if (viewMode !== 'multiple') return
        updateMultipleSlots(prev => {
            if (images.length === 0) return []
            if (prevViewMode !== 'multiple') return createMultipleSlots(images.length, currentIndexRef.current)
            if (!prev.length) return createMultipleSlots(images.length, currentIndexRef.current)
            return fillMultipleSlots(prev, images.length, currentIndexRef.current)
        })
    }, [images.length, updateMultipleSlots, viewMode])

    const syncCurrentIndex = useCallback((nextIndex) => {
        currentIndexRef.current = nextIndex
        setCurrentIndex(nextIndex)
    }, [setCurrentIndex])

    const updateImageAtIndex = useCallback((imageIndex, updater) => {
        const currentImages = imagesRef.current || []
        if (!currentImages[imageIndex]) return null
        const newImages = [...currentImages]
        const nextImage = typeof updater === 'function' ? updater(newImages[imageIndex]) : updater
        newImages[imageIndex] = nextImage
        imagesRef.current = newImages
        setImages(newImages)
        return nextImage
    }, [setImages, imagesRef])

    const getCurrentAnnotations = useCallback((image) => {
        return image?.annotations || []
    }, [])

    // 关闭和切图共用这套“不通过必须有标注”的校验
    const validateRejectedAnnotations = useCallback((image) => {
        const currentAnnotations = getCurrentAnnotations(image)
        if (requireRejectedAnnotationBeforeSwitch && image?.status === 'rejected' && currentAnnotations.length === 0) {
            message.warning('选择不通过时，需要标注修改内容')
            return false
        }
        if (image?.status === 'rejected' && currentAnnotations.some(item => (item?.time || item?.time === 0) && !item?.label && !item?.text && !item?.comment)) {
            message.warning('请补全截图帧描述问题')
            return false
        }
        return true
    }, [getCurrentAnnotations, requireRejectedAnnotationBeforeSwitch])

    // 切换图片前校验
    const validateBeforeSwitch = useCallback((image) => {
        if (image?.readOnly) return true
        if (!requireRejectedAnnotationBeforeSwitch) return true
        return validateRejectedAnnotations(image)
    }, [requireRejectedAnnotationBeforeSwitch, validateRejectedAnnotations])

    // 统一判断离开当前图时是否需要真正调提交接口
    const needCommitOnLeave = useCallback((image) => {
        if (image?.readOnly) return false
        if (!image?.shouldCommitOnLeave) return false
        if (submitEmptyStatusOnLeave) return true
        return image?.status === 'approved' || image?.status === 'rejected' || image?.status === 'canceled'
    }, [submitEmptyStatusOnLeave])

    const syncImageAfterCommit = useCallback((image, imageIndex) => {
        setImages(prev => {
            if (!prev[imageIndex]) return prev
            const newImages = [...prev]
            newImages[imageIndex] = {
                ...newImages[imageIndex],
                annotations: image?.status === 'rejected' ? (newImages[imageIndex]?.annotations || []) : [],
                shouldCommitOnLeave: false,
            }
            imagesRef.current = newImages
            return newImages
        })
    }, [])

    const commitCurrentImage = useCallback(async (image, imageIndex) => {
        if (!needCommitOnLeave(image)) return true
        try {
            setIsSwitching(true)
            await onStatusChange?.({
                id: image?.id,
                status: image?.status,
                annotation: image?.status === 'rejected' ? image?.annotations || [] : null,
            }, image?._raw)
            syncImageAfterCommit(image, imageIndex)
            return true
        } catch (err) {
            console.error(err)
            return false
        } finally {
            setIsSwitching(false)
        }
    }, [needCommitOnLeave, onStatusChange, syncImageAfterCommit])

    // 切换图片
    const handleIndexChange = useCallback(async (nextIndex) => {
        if (isSwitching) return
        const activeIndex = currentIndexRef.current
        const activeImages = imagesRef.current || []
        if (nextIndex === activeIndex) return
        if (nextIndex < 0 || nextIndex >= activeImages.length) return
        const image = activeImages[activeIndex] || {}
        if (!validateBeforeSwitch(image)) return
        const committed = await commitCurrentImage(image, activeIndex)
        if (!committed) return
        const activeExtraImage = getActiveExtraImage(image)
        if (activeExtraImage) {
            updateImageAtIndex(nextIndex, (nextImage) => {
                const matchedExtraIndex = findMatchedExtraImageIndex(nextImage, image, activeExtraImage)
                if (!Number.isInteger(matchedExtraIndex)) return nextImage
                if ((nextImage?._activeExtraIndex || 0) === matchedExtraIndex) return nextImage
                return {
                    ...nextImage,
                    _activeExtraIndex: matchedExtraIndex,
                }
            })
        }
        syncCurrentIndex(nextIndex)
    }, [commitCurrentImage, isSwitching, syncCurrentIndex, updateImageAtIndex, validateBeforeSwitch])

    const focusMultipleImage = useCallback((imageIndex) => {
        if (!Number.isInteger(imageIndex)) return
        if (!imagesRef.current?.[imageIndex]) return
        syncCurrentIndex(imageIndex)
    }, [imagesRef, syncCurrentIndex])

    // 多选槽前进
    const advanceMultipleSlot = useCallback((imageIndex) => {
        const activeSlots = multipleSlotsRef.current || []
        const targetSlot = activeSlots.find(slot => slot.imageIndex === imageIndex)
        const targetImage = (imagesRef.current || [])[imageIndex] || {}
        if (!targetSlot || targetImage.pinned) return false
        const occupiedIndices = activeSlots
            .filter(slot => slot.slotIndex !== targetSlot.slotIndex)
            .map(slot => slot.imageIndex)
        const nextImageIndex = findNextAvailableImageIndex(imagesRef.current?.length || 0, imageIndex, occupiedIndices)
        if (!Number.isInteger(nextImageIndex)) return false
        updateMultipleSlots(prev => prev.map(slot => (
            slot.slotIndex === targetSlot.slotIndex
                ? { ...slot, imageIndex: nextImageIndex }
                : slot
        )))
        if (currentIndexRef.current === imageIndex) {
            syncCurrentIndex(nextImageIndex)
        }
        return true
    }, [imagesRef, syncCurrentIndex, updateMultipleSlots])

    // 多选槽聚焦
    const handleMultipleFocus = useCallback((imageIndex) => {
        focusMultipleImage(imageIndex)
    }, [focusMultipleImage])

    // 多选槽注改变
    const handleMultipleAnnotationChange = useCallback((imageIndex, annotations) => {
        updateImageAtIndex(imageIndex, (image) => ({
            ...image,
            annotations,
            shouldCommitOnLeave: true,
        }))
    }, [updateImageAtIndex])

    // 多选槽状态改变
    const handleMultipleStatusChange = useCallback(async (imageIndex, status) => {
        if (isSwitching) return
        updateImageAtIndex(imageIndex, (image) => ({
            ...image,
            status,
            shouldCommitOnLeave: true,
        }))
        if (status !== 'approved') return
        const image = (imagesRef.current || [])[imageIndex] || {}
        const committed = await commitCurrentImage(image, imageIndex)
        if (!committed) return
        advanceMultipleSlot(imageIndex)
    }, [advanceMultipleSlot, commitCurrentImage, imagesRef, isSwitching, updateImageAtIndex])

    // 确认多选槽
    const handleMultipleConfirm = useCallback(async (imageIndex) => {
        if (isSwitching) return
        const image = (imagesRef.current || [])[imageIndex] || {}
        const shouldShowSavedMessage = needCommitOnLeave(image)
        if (!validateBeforeSwitch(image)) return
        const committed = await commitCurrentImage(image, imageIndex)
        if (!committed) return
        const advanced = advanceMultipleSlot(imageIndex)
        if (!advanced && shouldShowSavedMessage) message.success('已保存')
    }, [advanceMultipleSlot, commitCurrentImage, imagesRef, isSwitching, needCommitOnLeave, validateBeforeSwitch])

    // 多选槽钉住/取消钉住
    const handleMultiplePinToggle = useCallback((slotIndex) => {
        const activeSlots = multipleSlotsRef.current || []
        const targetSlot = activeSlots.find(slot => slot.slotIndex === slotIndex)
        if (!targetSlot) return
        const targetImage = (imagesRef.current || [])[targetSlot.imageIndex] || {}
        if (!targetImage.pinned) {
            const pinnedCount = activeSlots.filter(slot => ((imagesRef.current || [])[slot.imageIndex] || {}).pinned).length
            if (pinnedCount >= MULTIPLE_MAX_PIN_COUNT) {
                message.warning('最多同时钉住2张图')
                return
            }
        }
        updateImageAtIndex(targetSlot.imageIndex, (image) => ({
            ...image,
            pinned: !image?.pinned,
        }))
    }, [imagesRef, multipleSlotsRef, updateImageAtIndex])

    // 多选槽选择
    const handleMultipleSelect = useCallback(async (nextIndex) => {
        if (isSwitching) return
        if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= (imagesRef.current || []).length) return
        const activeSlots = multipleSlotsRef.current || []
        if (activeSlots.some(slot => slot.imageIndex === nextIndex)) {
            focusMultipleImage(nextIndex)
            return
        }
        const targetSlot = activeSlots.find(slot => slot.imageIndex === currentIndexRef.current && !((imagesRef.current || [])[slot.imageIndex] || {}).pinned)
            || activeSlots.find(slot => !((imagesRef.current || [])[slot.imageIndex] || {}).pinned)
        if (!targetSlot) {
            message.warning('最多同时钉住2张图，请先取消一个钉住状态')
            return
        }
        const currentImageIndex = targetSlot.imageIndex
        const currentSlotImage = (imagesRef.current || [])[currentImageIndex] || {}
        if (!validateBeforeSwitch(currentSlotImage)) return
        const committed = await commitCurrentImage(currentSlotImage, currentImageIndex)
        if (!committed) return
        updateMultipleSlots(prev => prev.map(slot => (
            slot.slotIndex === targetSlot.slotIndex
                ? { ...slot, imageIndex: nextIndex }
                : slot
        )))
        syncCurrentIndex(nextIndex)
    }, [commitCurrentImage, currentIndexRef, focusMultipleImage, imagesRef, isSwitching, syncCurrentIndex, updateMultipleSlots, validateBeforeSwitch])

    // 拖拽到指定多选槽
    const handleMultipleDrop = useCallback(async (nextIndex, slotIndex) => {
        if (isSwitching) return
        if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= (imagesRef.current || []).length) return
        const activeSlots = multipleSlotsRef.current || []
        const targetSlot = activeSlots.find(slot => slot.slotIndex === slotIndex)
        if (!targetSlot) return
        const targetImage = (imagesRef.current || [])[targetSlot.imageIndex] || {}
        if (targetImage.pinned) {
            message.warning('当前槽位已钉住，无法替换')
            return
        }
        if (targetSlot.imageIndex === nextIndex) {
            focusMultipleImage(nextIndex)
            return
        }
        const sourceSlot = activeSlots.find(slot => slot.slotIndex !== slotIndex && slot.imageIndex === nextIndex)
        if (sourceSlot) {
            updateMultipleSlots(prev => prev.map(slot => {
                if (slot.slotIndex === slotIndex) {
                    return {
                        ...slot,
                        imageIndex: nextIndex,
                    }
                }
                if (slot.slotIndex === sourceSlot.slotIndex) {
                    return {
                        ...slot,
                        imageIndex: targetSlot.imageIndex,
                    }
                }
                return slot
            }))
            syncCurrentIndex(nextIndex)
            return
        }
        const currentSlotImage = (imagesRef.current || [])[targetSlot.imageIndex] || {}
        if (!validateBeforeSwitch(currentSlotImage)) return
        const committed = await commitCurrentImage(currentSlotImage, targetSlot.imageIndex)
        if (!committed) return
        updateMultipleSlots(prev => prev.map(slot => (
            slot.slotIndex === slotIndex
                ? { ...slot, imageIndex: nextIndex }
                : slot
        )))
        syncCurrentIndex(nextIndex)
    }, [commitCurrentImage, focusMultipleImage, imagesRef, isSwitching, syncCurrentIndex, updateMultipleSlots, validateBeforeSwitch])

    // 多图模式键盘批量切换
    const handleMultipleBatchShift = useCallback(async (direction) => {
        if (isSwitching) return
        const activeSlots = multipleSlotsRef.current || []
        const currentImages = imagesRef.current || []
        const unpinnedSlots = activeSlots
            .filter(slot => !currentImages[slot.imageIndex]?.pinned)
            .sort((a, b) => a.slotIndex - b.slotIndex)

        if (!unpinnedSlots.length) {
            message.warning('当前图片均已钉住，请先取消一个钉住状态')
            return
        }

        const pinnedIndices = activeSlots
            .filter(slot => currentImages[slot.imageIndex]?.pinned)
            .map(slot => slot.imageIndex)
        const availableIndices = currentImages
            .map((_, index) => index)
            .filter(index => !pinnedIndices.includes(index))
        const currentUnpinnedIndices = unpinnedSlots.map(slot => slot.imageIndex)
        const currentPositions = currentUnpinnedIndices
            .map(index => availableIndices.indexOf(index))
            .filter(index => index >= 0)
            .sort((a, b) => a - b)

        if (!currentPositions.length) return

        const pageSize = unpinnedSlots.length
        const currentStart = currentPositions[0]
        const maxStart = Math.max(0, availableIndices.length - pageSize)

        if (direction > 0 && currentStart >= maxStart) return
        if (direction < 0 && currentStart <= 0) return

        const targetStart = direction > 0
            ? Math.min(maxStart, currentStart + pageSize)
            : Math.max(0, currentStart - pageSize)
        const targetIndices = availableIndices.slice(targetStart, targetStart + pageSize)

        if (targetIndices.length !== pageSize) return

        const leavingIndices = currentUnpinnedIndices.filter(index => !targetIndices.includes(index))
        for (const imageIndex of leavingIndices) {
            const image = currentImages[imageIndex] || {}
            if (!validateBeforeSwitch(image)) return
            const committed = await commitCurrentImage(image, imageIndex)
            if (!committed) return
        }

        const activeIndex = currentIndexRef.current
        const activeSlot = activeSlots.find(slot => slot.imageIndex === activeIndex)
        let nextFocusIndex = activeIndex

        if (!currentImages[activeIndex]?.pinned) {
            const activeSlotIndex = unpinnedSlots.findIndex(slot => slot.slotIndex === activeSlot?.slotIndex)
            nextFocusIndex = targetIndices[activeSlotIndex] ?? targetIndices[0] ?? activeIndex
        }

        updateMultipleSlots(prev => {
            let targetPointer = 0
            return prev.map(slot => {
                if (!unpinnedSlots.some(item => item.slotIndex === slot.slotIndex)) {
                    return slot
                }
                const nextImageIndex = targetIndices[targetPointer]
                targetPointer += 1
                return {
                    ...slot,
                    imageIndex: nextImageIndex,
                }
            })
        })

        syncCurrentIndex(nextFocusIndex)
    }, [commitCurrentImage, currentIndexRef, imagesRef, isSwitching, multipleSlotsRef, syncCurrentIndex, updateMultipleSlots, validateBeforeSwitch])

    // 多选槽数据
    const multipleItems = useMemo(() => {
        return multipleSlots
            .filter(slot => Number.isInteger(slot?.imageIndex) && images[slot.imageIndex])
            .map(slot => {
                const occupiedIndices = multipleSlots
                    .filter(item => item.slotIndex !== slot.slotIndex)
                    .map(item => item.imageIndex)
                return {
                    ...slot,
                    image: images[slot.imageIndex],
                    isActive: currentIndex === slot.imageIndex,
                    pinned: !!images[slot.imageIndex]?.pinned,
                    canAdvance: !!images[slot.imageIndex]?.pinned
                        ? false
                        : Number.isInteger(findNextAvailableImageIndex(images.length, slot.imageIndex, occupiedIndices)),
                }
            })
    }, [currentIndex, images, multipleSlots])

    // 返回时复用与切图一致的提交规则，但仅对已选状态执行关闭前提交
    const handleCancel = useCallback(async () => {
        if (isSwitching) return
        if (viewMode === 'multiple') {
            const visibleIndices = [...new Set((multipleSlotsRef.current || []).map(slot => slot.imageIndex))]
            for (const imageIndex of visibleIndices) {
                const image = (imagesRef.current || [])[imageIndex] || {}
                if (!needCommitOnLeave(image)) continue
                if (!validateBeforeSwitch(image)) return
                const committed = await commitCurrentImage(image, imageIndex)
                if (!committed) return
            }
            clearTemporaryMaterials()
            onCancel?.()
            return
        }
        const activeIndex = currentIndexRef.current
        const image = (imagesRef.current || [])[activeIndex] || {}
        if (!needCommitOnLeave(image)) {
            clearTemporaryMaterials()
            onCancel?.()
            return
        }
        if (!validateBeforeSwitch(image)) return
        const committed = await commitCurrentImage(image, activeIndex)
        if (!committed) return
        clearTemporaryMaterials()
        onCancel?.()
    }, [clearTemporaryMaterials, commitCurrentImage, imagesRef, isSwitching, needCommitOnLeave, onCancel, validateBeforeSwitch, viewMode])

    // 上一张图片
    const handlePrev = useCallback(() => {
        return handleIndexChange(currentIndexRef.current - 1)
    }, [handleIndexChange])
    
    // 下一张图片
    const handleNext = useCallback(() => {
        return handleIndexChange(currentIndexRef.current + 1)
    }, [handleIndexChange])
    
    // 选择图片
    const handleSelect = useCallback((index) => {
        if (viewMode === 'multiple') {
            return handleMultipleSelect(index)
        }
        return handleIndexChange(index)
    }, [handleIndexChange, handleMultipleSelect, viewMode])
    
    // 标注改变
    const handleAnnotationChange = useCallback(async (annotations) => {
        const activeIndex = currentIndexRef.current
        setImages(prev => {
            const newImages = [...prev]
            newImages[activeIndex] = {
                ...newImages[activeIndex],
                annotations,
                shouldCommitOnLeave: true,
            }
            imagesRef.current = newImages
            return newImages
        })
    }, [])
    
    // 状态改变
    const handleStatusChange = useCallback(async (status) => {
        const activeIndex = currentIndexRef.current
        setImages(prev => {
            const newImages = [...prev]
            newImages[activeIndex] = {
                ...newImages[activeIndex],
                status,
                shouldCommitOnLeave: true,
            }
            imagesRef.current = newImages
            return newImages
        })
    }, [])

    const handleMaskVisibleChange = useCallback((checked) => {
        const activeIndex = currentIndexRef.current
        setImages(prev => {
            const newImages = [...prev]
            if (!newImages[activeIndex]) return prev
            newImages[activeIndex] = {
                ...newImages[activeIndex],
                _showMask: checked,
            }
            imagesRef.current = newImages
            return newImages
        })
    }, [])
    
    // Confirm操作
    const handleConfirm = useCallback(() => {
        if (isSwitching) return
        if (hasNext) {
            handleNext()
        } else {
            handleCancel()
        }
    }, [isSwitching, hasNext, handleNext, handleCancel])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isEditableKeyboardTarget(event)) return
            switch (event.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault()
                    event.stopPropagation()
                    if (viewMode === 'multiple') {
                        handleMultipleBatchShift(-1)
                        return
                    }
                    if (hasPrev) {
                        handlePrev()
                    }
                    return
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault()
                    event.stopPropagation()
                    if (viewMode === 'multiple') {
                        handleMultipleBatchShift(1)
                        return
                    }
                    if (hasNext) {
                        handleNext()
                    }
                    return
                default:
                    return
            }
        }

        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [handleMultipleBatchShift, handleNext, handlePrev, hasNext, hasPrev, viewMode])

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key !== 'Escape' || event.repeat || event.defaultPrevented) return
            if (isEditableKeyboardTarget(event)) return
            event.preventDefault()
            event.stopPropagation()
            // 外层容器禁用原生 ESC 后，由组件统一完成离开校验、状态提交和关闭。
            handleCancel()
        }

        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [handleCancel])

    // 反馈改变
    const handleFeedbackChange = useCallback(async (like, feedbackText, feedbackId) => {
        const activeIndex = currentIndexRef.current
        const image = (imagesRef.current || [])[activeIndex] || {}
        const id = image.id
        if (feedbackId) {
            await onFeedback?.({id, like, feedbackId, feedbackText}, image._raw)
            if (!isMountedRef.current) return
            setImages(prev => {
                const newImages = [...prev]
                if (!newImages[activeIndex]) return prev
                newImages[activeIndex] = {
                    ...newImages[activeIndex],
                    feedback: {
                        id: feedbackId,
                        like: like ? 1 : 0,
                        description: feedbackText,
                    }
                }
                imagesRef.current = newImages
                return newImages
            })
        } else {
            const feedbackId = await onFeedback?.({id, like, feedbackText}, image._raw)
            if (!isMountedRef.current) return
            setImages(prev => {
                const newImages = [...prev]
                if (!newImages[activeIndex]) return prev
                newImages[activeIndex] = {
                    ...newImages[activeIndex],
                    feedback: {
                        id: feedbackId,
                        like: like ? 1 : 0,
                        description: feedbackText,
                    }
                }
                imagesRef.current = newImages
                return newImages
            })
        }
    }, [onFeedback])

    return (
        <div className={styles.PictureViewer}>
            <div className={styles.LeftWrapper}>
                <PicViewerImageInfo
                    onCancel={handleCancel}
                    image={previewImage}
                    enableTemporaryMaterials={enableTemporaryMaterials}
                    onTemporaryMaterialsAdd={addTemporaryMaterials}
                    onTemporaryMaterialRemove={removeTemporaryMaterial}
                    activeExtraIndex={previewImage._activeExtraIndex || 0}
                    maskVisible={!!currentImage._showMask}
                    onExtraImageSelect={(index) => {
                        const selected = previewImage.extraImages?.[index]
                        if (selected?._temporaryMaterial) {
                            selectTemporaryMaterial(selected.id)
                            if (viewMode === 'single' || viewMode === 'multiple') setViewMode('parallel')
                            return
                        }
                        selectTemporaryMaterial(null)
                        setImages(prev => {
                            const newImages = [...prev]
                            newImages[currentIndex] = {
                                ...newImages[currentIndex],
                                _activeExtraIndex: index,
                            }
                            imagesRef.current = newImages
                            return newImages
                        })
                    }}
                    onMaskVisibleChange={handleMaskVisibleChange}
                    onFeedback={handleFeedbackChange}
                />
            </div>
            <div className={styles.CenterWrapper}>
                <PicViewerCenter
                    image={previewImage}
                    onDownload={onDownload}
                    extraButtons={extraButtons}
                    onPrev={handlePrev}
                    onNext={handleNext}
                    // 单图操作
                    onAnnotationChange={handleAnnotationChange}
                    onStatusChange={handleStatusChange}
                    onConfirm={handleConfirm}
                    // 多选槽操作
                    multipleItems={multipleItems}
                    onMultipleFocus={handleMultipleFocus}
                    onMultipleAnnotationChange={handleMultipleAnnotationChange}
                    onMultipleStatusChange={handleMultipleStatusChange}
                    onMultipleConfirm={handleMultipleConfirm}
                    onMultiplePinToggle={handleMultiplePinToggle}
                    onMultipleDrop={handleMultipleDrop}
                    onMultipleBatchShift={handleMultipleBatchShift}
                />
            </div>
            <div className={styles.RightWrapper}>
                {
                    rightSideRender ?
                    rightSideRender(currentImage) : null
                }
                <PicViewerImageList
                    viewMode={viewMode}
                    images={images}
                    currentIndex={currentIndex}
                    multipleSlots={multipleSlots}
                    total={images.length}
                    onSelect={handleSelect}
                />
            </div>
        </div>
    )
}

export default (props) => (
    <PictureViewerContextProvider {...props}>
        <PictureViewer {...props} />
    </PictureViewerContextProvider>
)

export { RightSideRender }


// 获取有序的图片索引
function getOrderedImageIndices(imagesLength, startIndex = 0) {
    if (imagesLength <= 0) return []
    const safeStartIndex = Math.min(Math.max(startIndex, 0), imagesLength - 1)
    const orderedIndices = []
    for (let index = safeStartIndex; index < imagesLength; index += 1) {
        orderedIndices.push(index)
    }
    for (let index = 0; index < safeStartIndex; index += 1) {
        orderedIndices.push(index)
    }
    return orderedIndices
}

// 创建多选槽槽位
function createMultipleSlots(imagesLength, startIndex = 0) {
    return getOrderedImageIndices(imagesLength, startIndex)
        .slice(0, MULTIPLE_SLOT_COUNT)
        .map((imageIndex, slotIndex) => ({
            slotIndex,
            imageIndex,
        }))
}

// 填充多选槽槽位
function fillMultipleSlots(slots = [], imagesLength, startIndex = 0) {
    const normalizedSlots = slots
        .filter(slot => Number.isInteger(slot?.imageIndex) && slot.imageIndex >= 0 && slot.imageIndex < imagesLength)
        .slice(0, MULTIPLE_SLOT_COUNT)
        .map((slot, slotIndex) => ({
            slotIndex,
            imageIndex: slot.imageIndex,
        }))
    const usedIndices = normalizedSlots.map(slot => slot.imageIndex)
    const missingIndices = getOrderedImageIndices(imagesLength, startIndex)
        .filter(index => !usedIndices.includes(index))
        .slice(0, Math.max(0, MULTIPLE_SLOT_COUNT - normalizedSlots.length))
    return [
        ...normalizedSlots,
        ...missingIndices.map((imageIndex, extraIndex) => ({
            slotIndex: normalizedSlots.length + extraIndex,
            imageIndex,
        })),
    ]
}

// 查找下一个可用的图片索引
function findNextAvailableImageIndex(imagesLength, currentImageIndex, excludedIndices = []) {
    for (let nextIndex = currentImageIndex + 1; nextIndex < imagesLength; nextIndex += 1) {
        if (!excludedIndices.includes(nextIndex)) {
            return nextIndex
        }
    }
    return null
}

function getActiveExtraImage(image) {
    const activeExtraIndex = image?._activeExtraIndex || 0
    return image?.extraImages?.[activeExtraIndex] || null
}

function findMatchedExtraImageIndex(nextImage, currentImage, activeExtraImage) {
    const nextExtraImages = nextImage?.extraImages || []
    if (!nextExtraImages.length || !activeExtraImage) return null

    const activeMd5 = getExtraImageMd5(activeExtraImage)
    if (activeMd5) {
        const md5MatchedIndex = nextExtraImages.findIndex(item => getExtraImageMd5(item) === activeMd5)
        if (md5MatchedIndex >= 0) return md5MatchedIndex
    }

    const activeLabel = activeExtraImage?.label
    if (!activeLabel) return null

    const currentActiveExtraIndex = currentImage?._activeExtraIndex || 0
    const sameLabelOrderIndex = getSameLabelOrderIndex(currentImage?.extraImages, currentActiveExtraIndex, activeLabel)
    const sameLabelIndices = nextExtraImages.reduce((result, item, index) => {
        if (item?.label === activeLabel) result.push(index)
        return result
    }, [])

    if (!sameLabelIndices.length) return null
    return sameLabelIndices[sameLabelOrderIndex] ?? sameLabelIndices[0] ?? null
}

function getSameLabelOrderIndex(extraImages = [], activeExtraIndex = 0, label) {
    if (!label) return 0
    let orderIndex = 0
    for (let index = 0; index < extraImages.length; index += 1) {
        if (extraImages[index]?.label !== label) continue
        if (index === activeExtraIndex) return orderIndex
        orderIndex += 1
    }
    return 0
}

function getExtraImageMd5(extraImage) {
    const url = extraImage?.url
    if (!url || typeof url !== 'string') return null
    const queryString = url.split('?')[1] || ''
    if (!queryString) return null
    const searchParams = new URLSearchParams(queryString)
    return searchParams.get('md5')
}

function isEditableKeyboardTarget(event) {
    const target = event?.target
    const activeElement = document.activeElement
    return isEditableElement(target) || isEditableElement(activeElement)
}

function isEditableElement(element) {
    if (!(element instanceof HTMLElement)) return false
    if (element.isContentEditable) return true
    const tagName = element.tagName?.toLowerCase()
    if (tagName === 'textarea') return true
    if (tagName !== 'input') return false
    const inputType = (element.getAttribute('type') || 'text').toLowerCase()
    return !['button', 'checkbox', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(inputType)
}
